import React, { createContext, ReactNode, useContext } from "react";
import { ModalProps } from "@components";
import { Modal } from "@components/Modal/Modal";

interface SharedModalContextProps {
  openModal: (
    options: {
      modalComponent: ReactNode;
    } & Omit<ModalProps, "children" | "isOpen" | "onClose">
  ) => void;
  closeModal: () => void;
  isOpen: boolean;
}

const SharedModalContext = createContext<SharedModalContextProps | undefined>(
  undefined
);

export const useSharedModalContext = (): SharedModalContextProps => {
  const context = useContext(SharedModalContext);
  if (!context) {
    throw new Error("useSharedModal must be used within a SharedModalProvider");
  }
  return context;
};

export const SharedModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [modalComponent, setModalComponent] = React.useState<ReactNode>();
  const [modalProps, setModalProps] =
    React.useState<Omit<ModalProps, "children" | "isOpen" | "onClose">>();

  const openModal = (
    options: {
      modalComponent: ReactNode;
    } & Omit<ModalProps, "children" | "isOpen" | "onClose">
  ) => {
    setModalComponent(options.modalComponent);
    setModalProps(options);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return (
    <SharedModalContext.Provider value={{ openModal, closeModal, isOpen }}>
      {children}
      <Modal isOpen={isOpen} onClose={closeModal} {...modalProps}>
        {modalComponent}
      </Modal>
    </SharedModalContext.Provider>
  );
};
